/**
 * ASR provider backed by the browser Web Speech API
 * (`webkitSpeechRecognition` / `SpeechRecognition`).
 *
 * Pure (no React/store/network of our own — the browser engine may use the
 * network, but THIS layer never touches `fetch`). Vendor error strings are
 * normalized to {@link VoiceErrorCode} → i18n key via {@link mapRecognitionError}.
 */
import {
  type SpeechRecognitionCallbacks,
  type SpeechRecognitionOptions,
  type SpeechRecognitionProvider,
  VoiceError,
  type VoiceErrorCode,
} from './types'

const DEFAULT_LANG = 'pt-BR'

/**
 * Resolve the recognition constructor from `window`, preferring the
 * unprefixed name. Returns `null` when the API is absent (SSR, Firefox, …).
 */
function getRecognitionCtor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null
}

/**
 * Map a browser recognition error code (or a thrown `NotReadableError`-style
 * name) onto our stable {@link VoiceErrorCode}.
 */
export function mapRecognitionError(vendor: string): VoiceErrorCode {
  switch (vendor) {
    case 'not-allowed':
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'not-allowed'
    case 'service-not-allowed':
      return 'service-not-allowed'
    case 'no-speech':
      return 'no-speech'
    case 'aborted':
      return 'aborted'
    // getUserMedia surfaces a hardware-busy mic as NotReadableError; the
    // Speech API can bubble the same condition when it opens the device.
    case 'audio-capture':
    case 'NotReadableError':
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'audio-capture'
    case 'network':
      return 'network'
    case 'language-not-supported':
    case 'bad-grammar':
      return 'language-not-supported'
    default:
      return 'unknown'
  }
}

export class WebSpeechRecognitionProvider implements SpeechRecognitionProvider {
  readonly id = 'web-speech'

  private recognition: SpeechRecognition | null = null
  private active = false
  /** Guards against double-`onEnd` (stop() + natural end racing). */
  private ended = false

  isSupported(): boolean {
    return getRecognitionCtor() !== null
  }

  start(callbacks: SpeechRecognitionCallbacks, options: SpeechRecognitionOptions = {}): void {
    // A session is already running — ignore (single-session contract).
    if (this.active) return

    const Ctor = getRecognitionCtor()
    if (!Ctor) {
      // The session never opened. Report the error AND fire onEnd so a consumer
      // that toggled a "listening" indicator on start clears it (invariant:
      // every start terminates in onEnd, or error + onEnd).
      callbacks.onError(new VoiceError('not-supported'))
      callbacks.onEnd?.()
      return
    }

    let recognition: SpeechRecognition
    try {
      recognition = new Ctor()
    } catch {
      callbacks.onError(new VoiceError('not-supported'))
      callbacks.onEnd?.()
      return
    }

    recognition.lang = options.lang ?? DEFAULT_LANG
    recognition.interimResults = options.interimResults ?? true
    recognition.continuous = options.continuous ?? false
    recognition.maxAlternatives = 1

    this.recognition = recognition
    this.active = true
    this.ended = false

    recognition.onstart = () => {
      callbacks.onStart?.()
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Emit only the newest results (resultIndex..end); interim flagged
      // isFinal=false so the consumer knows NOT to send it as a turn yet.
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const item = event.results[i]
        const alternative = item[0]
        if (!alternative) continue
        callbacks.onResult({
          transcript: alternative.transcript,
          isFinal: item.isFinal,
          confidence: Number.isFinite(alternative.confidence) ? alternative.confidence : undefined,
        })
      }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const code = mapRecognitionError(event.error)
      callbacks.onError(new VoiceError(code, event.error))
    }

    recognition.onend = () => {
      this.active = false
      this.recognition = null
      if (this.ended) return
      this.ended = true
      callbacks.onEnd?.()
    }

    try {
      recognition.start()
    } catch (err) {
      // start() throws (e.g. InvalidStateError) — normalize and report. The
      // session never opened, so onend will NOT fire from the engine; emit it
      // ourselves so the consumer's "listening" state is always torn down.
      this.active = false
      this.recognition = null
      const name = err instanceof Error ? err.name : 'unknown'
      callbacks.onError(new VoiceError(mapRecognitionError(name), name))
      callbacks.onEnd?.()
    }
  }

  stop(): void {
    this.recognition?.stop()
  }

  abort(): void {
    this.recognition?.abort()
  }
}
